/*
 * *****************************************************************************
 *     Copyright (C)  Motorola Solutions, INC.
 *     All Rights Reserved.
 *     Motorola Solutions Confidential Restricted.
 * *****************************************************************************
 */

import { Action, createFeature, createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { SortFunctions } from '../util/sort-functions';
import { Call, Cluster } from 'CalltakingCoreApi';
import {
    bargeIn,
    closeCall,
    deleteCall,
    deleteManyCalls,
    endObserveAgent,
    fetchAnalyticsHistoricalCallsSuccess,
    fetchRecentCallsSuccess,
    fetchRecentLockedCallsSuccess,
    holdComplete,
    holdCompleteFailure,
    linkedCallUpdate,
    lockCallSuccess,
    newCall,
    observeAgent,
    openCall,
    purgeAnalyticsHistoricalCall,
    purgeHistoricalCall,
    recordHistoricalCall,
    redialAbandoned,
    redialAbandonedFail,
    releaseCallComplete,
    releaseCallCompleteFailed,
    requestActiveCall,
    requestHold,
    requestHoldFail,
    requestReleaseCall,
    requestReleaseCallFail,
    setManyCalls,
    setObservationState,
    silentMonitorSuccess,
    toggleChat,
    unhold,
    unholdFail,
    unholdSuccess,
    unlockCallSuccess,
    updateActiveCall,
    updateCall,
    updateCallMultipleSubscriptions,
    updateSelectedCall
} from './call.actions';
import { HistoricalCall } from '../model/historical-call';
import dayjs from 'dayjs';
import { MultipleSubscriptionsStatus, Topic } from '../../user/model/multiple-subscriptions';
import { initSubscriptionState } from '../../configuration/+state/configuration.actions';
import {
    acknowledgeAbandonedComplete,
    acknowledgeAbandonedRequestFailed,
    acknowledgeAbandonedRequestSuccess
} from '../../user/+state/user.actions';

export type ObservationState = 'inactive' | 'active' | 'suspended';
export interface ActiveObservationState {
    state: ObservationState;
    observedUser: string | undefined;
    bargedIn: string | undefined; // callId
}

export interface CallState {
    multipleWebsocketSubscriptions: MultipleSubscriptionsStatus;
    local: LocalCallState;
    historical: LocalHistoricalCallState;
    remote: RemoteCallState;
    remoteHistorical: RemoteHistoricalCallState;
    analyticsHistorical: AnalyticsCallHistoryState;
    pendingActiveCallId: string | undefined;
    pendingHeldCall: string | undefined;
    pendingUnHold: string | undefined;
    pendingReleasedCall: string | undefined;
    heldCallCallback: Action | undefined;
    activeCallId: string | undefined;
    selectedCallId: string | undefined;
    chatOpen: boolean;
    deletedCache: {};
    lockedCalls: string[];
    openedCalls: string[];
    acknowledgedAbandonedCallId: string,
    pendingAcknowledgedAbandonedCallId: string | undefined;
    observationState: ActiveObservationState;
}

export interface LocalCallState extends EntityState<Call> {}

export interface LocalHistoricalCallState extends EntityState<Call> {}

export interface RemoteHistoricalCallState extends EntityState<Call> {}

export interface RemoteCallState extends EntityState<Call> {}

export interface AnalyticsCallHistoryState extends EntityState<HistoricalCall> {}

export const localAdapter: EntityAdapter<Call> = createEntityAdapter<Call>({
    selectId: (call: Call) => call.uuid,
    sortComparer: SortFunctions.defaultCallSort
});

export const localHistoricalAdapter: EntityAdapter<Call> = createEntityAdapter<Call>({
    selectId: (call: Call) => call.uuid,
    sortComparer: SortFunctions.newestReleasedSort
});

export const remoteHistoricalAdapter: EntityAdapter<Call> = createEntityAdapter<Call>({
    selectId: (call: Call) => call.uuid,
    sortComparer: SortFunctions.newestReleasedSort
});

export const remoteAdapter: EntityAdapter<Call> = createEntityAdapter<Call>({
    selectId: (call: Call) => call.uuid
});

export const analyticsHistoricalAdapter: EntityAdapter<HistoricalCall> = createEntityAdapter<HistoricalCall>({
    selectId: (record: HistoricalCall) => `${record.referenceKey}:${record.callId}`,
    sortComparer: SortFunctions.newestEndedAnalyticsSort
});

const initialLocalState: LocalCallState = localAdapter.getInitialState({});

const initialHistoricalState: LocalCallState = localAdapter.getInitialState({});

const initialRemoteState: RemoteCallState = localAdapter.getInitialState({});

const initialRemoteHistoricalState: RemoteHistoricalCallState = localAdapter.getInitialState({});

const initialAnalyticsHistoricalState: AnalyticsCallHistoryState = analyticsHistoricalAdapter.getInitialState({});


const initialState: CallState = {
    multipleWebsocketSubscriptions: {},
    local: initialLocalState,
    historical: initialHistoricalState,
    remote: initialRemoteState,
    remoteHistorical: initialRemoteHistoricalState,
    analyticsHistorical: initialAnalyticsHistoricalState,
    pendingActiveCallId: undefined,
    pendingHeldCall: undefined,
    pendingUnHold: undefined,
    heldCallCallback: undefined,
    pendingReleasedCall: undefined,
    activeCallId: undefined,
    selectedCallId: undefined,
    chatOpen: false,
    deletedCache: {},
    lockedCalls: [],
    openedCalls: [],
    acknowledgedAbandonedCallId: undefined,
    pendingAcknowledgedAbandonedCallId: undefined,
    observationState: {
        observedUser: undefined,
        state: 'inactive',
        bargedIn: undefined
    }
};

export const callFeature = createFeature({
    name: 'calls',
    reducer: createReducer(
        initialState,
        on(initSubscriptionState, (state, { config }) => {
            const multipleWebsocketSubscriptions: MultipleSubscriptionsStatus = {};
                config.clusters.forEach((c: Cluster) => {
                    multipleWebsocketSubscriptions[c.name] = {
                        [Topic.CALL_NEW]: false,
                        [Topic.CALL_EDIT]: false,
                        [Topic.CALL_DELETE]: false,
                    };
                });
            return {...state, multipleWebsocketSubscriptions};
        }),
        on(updateCallMultipleSubscriptions, (state, { topic, clusterName, status }): CallState => {
            const currentSubscriptions = state.multipleWebsocketSubscriptions;
            const subscriptions = {
                ...currentSubscriptions,
                [clusterName]: {
                    ...currentSubscriptions[clusterName],
                    [topic]: status
                }
            };
            return { ...state, multipleWebsocketSubscriptions: subscriptions };
        }),
        on(newCall, updateCall, (state, { call }) => {
            let existingEntity = state.local.entities[call.uuid];
            if (!existingEntity || dayjs(existingEntity.updatedTimestamp).isBefore(dayjs(call.updatedTimestamp))) {
                let pendingActiveCallId = state.pendingActiveCallId && call.uuid === state.pendingActiveCallId ? undefined : state.pendingActiveCallId;
                let activeCallId = state.pendingActiveCallId && call.uuid === state.pendingActiveCallId ? state.pendingActiveCallId : state.activeCallId;
                return { ...state, local: localAdapter.setOne(call, state.local), pendingActiveCallId: pendingActiveCallId, activeCallId: activeCallId };
            }
            return state;
        }),
        on(deleteCall, (state, { call }) => {
            let existingEntity = state.local.entities[call.uuid] as Call;
            if (existingEntity) {
                return {
                    ...state,
                    local: localAdapter.removeOne(call.uuid, state.local),
                    remote: remoteAdapter.removeMany((call) => Object.values(existingEntity.linkedAgencies)?.includes(call.uuid), state.remote)
                };
            }
            return state;
        }),
        on(recordHistoricalCall, (state, { call }) => {
            let activeCallId = call.uuid === state.activeCallId ? undefined : state.activeCallId;
            let linkedRemoteCallRecords = Object.values(call.linkedAgencies)
                .filter((remoteCallId) => state.remote.entities[remoteCallId])
                .map((remoteCallId) => state.remote.entities[remoteCallId] as Call);
            return {
                ...state,
                activeCallId: activeCallId,
                historical: localHistoricalAdapter.upsertOne(call, state.historical),
                remote: remoteAdapter.removeMany(
                    linkedRemoteCallRecords.map((call) => call.uuid),
                    state.remote
                ),
                remoteHistorical: remoteHistoricalAdapter.upsertMany(linkedRemoteCallRecords, state.remoteHistorical)
            };
        }),
        on(purgeHistoricalCall, (state, { uuid }) => {
            let localCallBeingPurged = state.historical.entities[uuid] as Call;
            return {
                ...state,
                historical: localHistoricalAdapter.removeOne(uuid, state.historical),
                remoteHistorical: remoteHistoricalAdapter.removeMany(
                    (call) => Object.values(localCallBeingPurged.linkedAgencies)?.includes(call.uuid),
                    state.remoteHistorical
                )
            };
        }),
        on(purgeAnalyticsHistoricalCall, (state, { referenceKey }) => {
            return { ...state, analyticsHistorical: analyticsHistoricalAdapter.removeMany((record) => record.referenceKey === referenceKey, state.analyticsHistorical) };
        }),
        on(setManyCalls, (state, { calls }) => {
            let newOrUpdatedCalls = calls.filter((call) =>
                !state.local.entities[call.uuid] || dayjs(state.local.entities[call.uuid].updatedTimestamp).isBefore(dayjs(call.updatedTimestamp)));
            return { ...state, local: localAdapter.setMany(newOrUpdatedCalls, state.local) };
        }),
        on(deleteManyCalls, (state, { callIds }) => {
            return { ...state, local: localAdapter.removeMany(callIds, state.local) };
        }),
        on(fetchAnalyticsHistoricalCallsSuccess, (state, { referenceKey, calls }) => {
            let analyticsHistoricalCalls = calls.map((record) => ({ ...record, referenceKey: referenceKey }));
            return { ...state, analyticsHistorical: analyticsHistoricalAdapter.upsertMany(analyticsHistoricalCalls, state.analyticsHistorical) };
        }),
        on(fetchRecentCallsSuccess, (state, { calls }): CallState => {
            return { ...state, historical: localHistoricalAdapter.upsertMany(calls, state.historical)};
        }),
        on(fetchRecentLockedCallsSuccess, (state, { lockedCallIds }): CallState => {
            return { ...state, lockedCalls: lockedCallIds };
        }),
        on(linkedCallUpdate, (state, { call }) => {
            let existingEntity = state.remote.entities[call.uuid] as Call;
            let existingHistoricalEntity = state.remoteHistorical.entities[call.uuid] as Call;
            if (existingHistoricalEntity && dayjs(existingHistoricalEntity.updatedTimestamp).isBefore(dayjs(call.updatedTimestamp))) {
                return { ...state, remoteHistorical: remoteHistoricalAdapter.upsertOne(call, state.remoteHistorical) };
            } else if (!existingEntity || (existingEntity && dayjs(existingEntity.updatedTimestamp).isBefore(dayjs(call.updatedTimestamp)))) {
                return { ...state, remote: remoteAdapter.upsertOne(call, state.remote) };
            }
            return state;
        }),
        on(requestActiveCall, (state, { callId }): CallState => {
            return { ...state, pendingActiveCallId: callId };
        }),
        on(updateActiveCall, (state, { callId }): CallState => {
            return { ...state, activeCallId: callId };
        }),
        on(unhold, (state, { callId }): CallState => {
            return { ...state, pendingUnHold: callId };
        }),
        on(unholdSuccess, unholdFail,(state): CallState => {
            return { ...state, pendingUnHold: undefined };
        }),
        on(requestHold, (state, { callId, callback }): CallState => {
            return { ...state, pendingHeldCall: callId, heldCallCallback: callback };
        }),
        on(requestReleaseCall, redialAbandoned, (state, { callId }): CallState => {
            return { ...state, pendingReleasedCall: callId };
        }),
        on(holdComplete, requestHoldFail, holdCompleteFailure, (state): CallState => {
            return { ...state, pendingHeldCall: undefined, heldCallCallback: undefined };
        }),
        on(requestReleaseCallFail, releaseCallComplete, releaseCallCompleteFailed, redialAbandonedFail, (state): CallState => {
            return { ...state, pendingReleasedCall: undefined };
        }),
        on(updateSelectedCall, (state, { callId }): CallState => {
            return { ...state, selectedCallId: callId };
        }),
        on(toggleChat, (state, { open }): CallState => {
            return { ...state, chatOpen: open };
        }),
        on(observeAgent, (state, { username }): CallState => {
            return {
                ...state,
                observationState: {
                    observedUser: username,
                    state: 'active',
                    bargedIn: undefined
                }
            };
        }),
        on(endObserveAgent, (state): CallState => {
            return {
                ...state,
                observationState: {
                    observedUser: undefined,
                    state: 'inactive',
                    bargedIn: undefined
                }
            };
        }),
        on(lockCallSuccess, (state, { callId }): CallState => {
            return { ...state, lockedCalls: [...state.lockedCalls, callId] };
        }),
        on(unlockCallSuccess, (state, { callId }): CallState => {
            return { ...state, lockedCalls: [...state.lockedCalls].filter((id) => id !== callId) };
        }),
        on(closeCall, (state, { callId }) => {
            const temp = [...state.openedCalls];
            const foundIndex = temp.indexOf(callId);
            let acknowledgedAbandonedCallId = state.acknowledgedAbandonedCallId;
            if (foundIndex > -1) {
                temp.splice(foundIndex, 1);
            }
            if (state.acknowledgedAbandonedCallId === callId) {
                acknowledgedAbandonedCallId = undefined;
            }
            return { ...state, openedCalls: temp, acknowledgedAbandonedCallId: acknowledgedAbandonedCallId };
        }),
        on(openCall, (state, { callId }): CallState => {
            return state.openedCalls.includes(callId) ?
                { ...state } :
                { ...state, openedCalls: [...state.openedCalls, callId] };
        }),
        on(acknowledgeAbandonedRequestSuccess, (state, { callId }): CallState => {
            return { ...state, pendingAcknowledgedAbandonedCallId: callId };
        }),
        on(acknowledgeAbandonedRequestFailed, (state): CallState => {
            return { ...state, pendingAcknowledgedAbandonedCallId: undefined };
        }),
        on(acknowledgeAbandonedComplete, (state, { callId }): CallState => {
            return { ...state, acknowledgedAbandonedCallId: callId, pendingAcknowledgedAbandonedCallId: undefined };
        }),
        on(
            bargeIn,
            (state, { callId }): CallState => ({
                ...state,
                observationState: {
                    ...state.observationState,
                    bargedIn: callId
                }
            })
        ),
        on(
            silentMonitorSuccess,
            (state): CallState => ({
                ...state,
                observationState: {
                    ...state.observationState,
                    bargedIn: undefined
                }
            })
        ),
        on(setObservationState, (state, { observationState }): CallState => {
            return {
                ...state,
                observationState: {
                    ...state.observationState,
                    state: observationState
                }
            };
        })
    )
});

export const localCallState = (state: CallState) => state.local;
export const localHistoricalCallState = (state: CallState) => state.historical;
export const remoteCallState = (state: CallState) => state.remote;
export const remoteHistoricalCallState = (state: CallState) => state.remoteHistorical;
export const analyticsHistoricalCallState = (state: CallState) => state.analyticsHistorical;

export const { selectAll: selectAllLocalCalls, selectEntities: selectLocalCallEntities, selectTotal: selectTotalCalls } = localAdapter.getSelectors();

export const { selectAll: selectAllHistoricalCalls, selectEntities: selectLocalHistoricalCallEntities } = localHistoricalAdapter.getSelectors();

export const { selectEntities: selectRemoteCallEntities } = remoteAdapter.getSelectors();

export const { selectEntities: selectHistoricalRemoteCallEntities } = remoteHistoricalAdapter.getSelectors();

export const { selectAll: selectAllAnalyticsHistoricalCalls, selectEntities: selectAnalyticsHistoricalEntities } = analyticsHistoricalAdapter.getSelectors();
